// Credit application and financing related reducers
import type {
    CalculationEndpoints,
    CalculationEndpointType,
    CreditApp,
    CreditScore,
    CreditScoreSource,
    CreditTiers,
    IPaymentTermsFallback,
    OfferType
} from '@makemydeal/dr-platform-types';
import { ApplicationCodes, BALLOON, CASH, FINANCE, LEASE } from '@makemydeal/dr-platform-types';
import type { AnyAction } from 'redux';
import * as types from '../types/actionTypes';

import { CALC_ENDPOINTS } from '../constants';

// Mirror of MV_AUTO_SELECT_PROGRAM_SAVED_BAGGAGE_NAME from @makemydeal/dr-dash-types
// (kept inline to avoid a cross-package dependency from offer-redux — dash-types
// already type-imports from offer-redux, so any offer-redux → dash-types import
// closes a cycle that @nx/enforce-module-boundaries blocks).
// A drift-guard test in dash-store's offerPayloadUtils.spec.ts asserts these match.
export const AUTO_SELECT_PROGRAM_SAVED_BAGGAGE_NAME = 'mv.autoSelectProgramSaved';
import { createEndpointsForAllTypes } from '../utils/paymentUtils';
import { selectCreditTiersFromPayload } from './actionMapper';
import { initialState } from './initialState';
import type { InitOfferCreditTiersActionPayload } from '../actions/payloadTypes';
import { reducerConfig } from './reducerConfig';

/**
 * Custom rate group reducer - manages the rate card channel for the deal
 */
export const customRateGroup = (state = initialState.customRateGroup, action: AnyAction): 'Instore' | 'Online' | null => {
    switch (action.type) {
        case types.INIT_SUCCESS: {
            const { sourceApplication } = action.meta || {};
            // MV always uses Instore regardless of what may have been stored in the baggage
            if (sourceApplication === ApplicationCodes.MV) return 'Instore';
            const fromPayload = action.payload.calculationDetails?.customRateGroup;
            if (fromPayload) return fromPayload;
            return state || null;
        }
        case types.APPLY_DEALER_PAYMENT_OVERRIDES: {
            // When MV applies dealer payment overrides, lock in Instore for the session.
            return 'Instore';
        }
        default:
            return state || null;
    }
};

/**
 * Credit application reducer - handles credit app state and decision details
 */
export function creditApp(state: CreditApp | undefined = initialState.creditApp, action: AnyAction) {
    switch (action.type) {
        case types.INIT_OFFER:
            return { ...state, ...action.payload.creditApp };
        case types.CREDIT_APP_SUBMITTED_AWAITING_DECISION:
            return { ...state, ...action.payload };
        case types.CREDIT_APP_CO_APPLICANT_EMAIL:
            return { ...state, ...action.payload };
        case types.SYNC_MESSAGE:
            return { ...state, ...action.message.offer.creditApp };
        case types.CREDIT_DECISION_DETAILS_RECEIVED: {
            return {
                ...state,
                decisionDetails: action.payload,
                shouldApplyDecisionDetails: true
            };
        }
        case types.PAYMENT_REQUESTED:
            return {
                ...state,
                shouldApplyDecisionDetails: false
            };
        case types.CREDIT_APP_SEARCH_MERGE_DEAL: {
            const creditApplication = action.payload?.dealStructure?.creditApplication;
            const creditAppReferenceId = creditApplication?.creditApplicationId ?? creditApplication?.creditAppReferenceId ?? '';
            if (!creditAppReferenceId) return state;
            return {
                ...state,
                creditAppReferenceId,
                status: 'Submitted'
            };
        }
        default:
            return state;
    }
}

/**
 * Credit application ID reducer
 */
export function creditAppId(state: string | undefined = initialState.creditAppId, action: AnyAction) {
    switch (action.type) {
        case types.INIT_SUCCESS:
        case types.DEALXG_GET_SUCCESS:
        case types.OFFER_SUBMISSION_SUCCESS: {
            return action.payload.offer.creditAppId || '';
        }
        case types.CREDIT_APP_SEARCH_MERGE_DEAL: {
            const creditApplication = action.payload?.dealStructure?.creditApplication;
            return creditApplication?.creditApplicationId ?? creditApplication?.creditAppReferenceId ?? state;
        }
        case types.CREDIT_APP_COPIED_BY_PARTICIPANTS_SAVE: {
            return action.payload.creditApplicationId;
        }
        default: {
            return state;
        }
    }
}

/**
 * Credit score reducer
 */
export function creditScore(state: CreditScore | undefined = initialState.creditScore, action: AnyAction) {
    switch (action.type) {
        case types.INIT_OFFER:
            return { ...state, ...action.payload.creditScore };
        case types.SYNC_MESSAGE:
            return { ...state, ...action.message.offer.creditScore };
        default:
            return state;
    }
}

/**
 * Credit score source reducer - tracks whether credit score is dealerDefault or selfReported
 */
export function creditScoreSource(
    state: CreditScoreSource | undefined = initialState.creditScoreSource,
    action: AnyAction
): CreditScoreSource | undefined {
    switch (action.type) {
        case types.INIT_OFFER:
            return action.payload.creditScoreSource ?? state;
        case types.UPDATE_CREDIT_SCORE_SOURCE:
            return action.payload ?? state;
        default:
            return state;
    }
}

/**
 * Credit tiers reducer - handles credit tier mappings and selections
 */
export function creditTiers(state: CreditTiers | undefined = initialState.creditTiers, action: AnyAction) {
    switch (action.type) {
        case types.INIT_OFFER:
            return { ...state, ...action.payload.creditTiers };
        case types.SYNC_MESSAGE:
            return { ...state, ...action.message.offer.creditTiers };
        case types.INIT_OFFER_CREDIT_TIERS:
            return { ...state, ...selectCreditTiersFromPayload(action.payload as InitOfferCreditTiersActionPayload) };
        default:
            return state;
    }
}

/**
 * Payment terms fallback reducer - handles fallback messaging for payment terms
 */
export function paymentTermsFallback(
    state: IPaymentTermsFallback | undefined = initialState.paymentTermsFallback,
    action: AnyAction
) {
    switch (action.type) {
        case types.INIT_SUCCESS: {
            return { ...state, ...action.payload.offer.paymentTermsFallback };
        }
        case types.DISMISS_TERMS_FALLBACK_MESSAGE: {
            const paymentTermsFallback = { ...state, hasMessageBeenShown: true };
            return { ...state, ...paymentTermsFallback };
        }
        default: {
            return state;
        }
    }
}

type CalculationEndpointsState = CalculationEndpoints & { autoSelectProgramSavedAt?: string | null };

const buildCalculationEndpointByOfferType = (args: {
    action: AnyAction;
    sourceApplication: string | undefined;
    isMvApp: boolean;
    isTemplateApplied: boolean | undefined;
    offerType: OfferType | undefined;
    initialCalculationEndpoint: CalculationEndpointType;
}) => {
    const { action, sourceApplication, isMvApp, isTemplateApplied, offerType, initialCalculationEndpoint } = args;

    if (isTemplateApplied && !isMvApp) {
        const withPaymentTerms = action.payload.template?.withPaymentTerms;
        return withPaymentTerms
            ? {
                  [FINANCE]: CALC_ENDPOINTS.userProgramQuotes,
                  [LEASE]: CALC_ENDPOINTS.programQuotes,
                  [CASH]: CALC_ENDPOINTS.programQuotes
              }
            : createEndpointsForAllTypes(CALC_ENDPOINTS.programQuotes);
    }
    if (sourceApplication === ApplicationCodes.SP) {
        return {
            ...(offerType && { [offerType]: initialCalculationEndpoint }),
            ...(offerType === BALLOON && { [FINANCE]: initialCalculationEndpoint })
        };
    }
    if (isMvApp && initialCalculationEndpoint === CALC_ENDPOINTS.programQuotes) {
        return {
            ...createEndpointsForAllTypes(CALC_ENDPOINTS.userProgramQuotes),
            ...(offerType && { [offerType]: CALC_ENDPOINTS.programQuotes })
        };
    }
    return createEndpointsForAllTypes(initialCalculationEndpoint);
};

function readAutoSelectProgramSavedMarker(action: AnyAction): string | undefined {
    const fromDealXgDetails = action.payload?.offer?.dealXgDetails?.autoSelectProgramSavedAt;
    if (typeof fromDealXgDetails === 'string' && fromDealXgDetails.length > 0) return fromDealXgDetails;
    const baggage = action.payload?.baggage;
    if (!Array.isArray(baggage)) return undefined;
    const entry = baggage.find((b: { name?: string }) => b?.name === AUTO_SELECT_PROGRAM_SAVED_BAGGAGE_NAME);
    const content = entry?.value?.content;
    return typeof content === 'string' && content.length > 0 ? content : undefined;
}

function buildInitCalculationEndpoints(action: AnyAction, previousLatchedMarker: string | null): CalculationEndpointsState {
    const initialCalculationEndpoint =
        action.payload.calculationDetails?.payment?.calculationEngine?.endpoint ?? CALC_ENDPOINTS.programQuotes;
    const sourceApplication = reducerConfig?.sourceApplication ?? action.meta?.sourceApplication;
    const isMvApp = sourceApplication === ApplicationCodes.MV;
    const isTemplateApplied = action.payload.template?.templateApplied;
    const offerType = action.payload.offer?.type;

    const calculationEndpointByOfferType = buildCalculationEndpointByOfferType({
        action,
        sourceApplication,
        isMvApp,
        isTemplateApplied,
        offerType,
        initialCalculationEndpoint
    });

    return {
        initialCalculationEndpoint,
        currentCalculationEndpoint: initialCalculationEndpoint,
        calculationEndpointByOfferType,
        autoSelectProgramSavedAt: readAutoSelectProgramSavedMarker(action) ?? previousLatchedMarker ?? null
    };
}

/**
 * Calculation endpoints reducer - manages payment calculation engine endpoints
 */
export function calculationEndpoints(
    state: CalculationEndpointsState = initialState.calculationEndpoints as CalculationEndpointsState,
    action: AnyAction
): CalculationEndpointsState {
    switch (action.type) {
        case types.INIT_SUCCESS:
            return buildInitCalculationEndpoints(action, state.autoSelectProgramSavedAt ?? null);
        case types.INIT_PENCIL_SUCCESS:
        case types.OFFER_SUBMISSION_SUCCESS: {
            if (state.autoSelectProgramSavedAt) return state;
            const marker = readAutoSelectProgramSavedMarker(action);
            return marker ? { ...state, autoSelectProgramSavedAt: marker } : state;
        }
        case types.UPDATED_CREDIT_DECISION_ENABLED: {
            const { offerType } = action.meta || {};
            const shouldUpdateCalcEndpointByOfferType = !!offerType;

            if (state.initialCalculationEndpoint === CALC_ENDPOINTS.userProgramQuotes) {
                return {
                    ...state,
                    currentCalculationEndpoint: CALC_ENDPOINTS.userProgramQuotes,
                    ...(shouldUpdateCalcEndpointByOfferType && {
                        calculationEndpointByOfferType: {
                            ...state.calculationEndpointByOfferType,
                            [offerType]: CALC_ENDPOINTS.userProgramQuotes
                        }
                    })
                };
            }
            const enabled = action.payload;
            const currentCalculationEndpoint = enabled ? CALC_ENDPOINTS.userProgramQuotes : CALC_ENDPOINTS.programQuotes;
            return {
                ...state,
                currentCalculationEndpoint,
                ...(shouldUpdateCalcEndpointByOfferType && {
                    calculationEndpointByOfferType: {
                        ...state.calculationEndpointByOfferType,
                        [offerType]: currentCalculationEndpoint
                    }
                })
            };
        }
        case types.UPDATE_CURRENT_CALCULATION_ENDPOINT: {
            const newCalculationEndpoint = action.payload;
            const offerType = action.meta?.offerType;

            if (!Object.values(CALC_ENDPOINTS).includes(newCalculationEndpoint)) {
                return state;
            }

            return {
                ...state,
                currentCalculationEndpoint: newCalculationEndpoint,
                calculationEndpointByOfferType: {
                    ...state.calculationEndpointByOfferType,
                    ...(offerType && { [offerType]: newCalculationEndpoint })
                }
            };
        }
        default:
            return state;
    }
}
